/**
 * Decorative sparkle trail, ported from the Full Bleed prototype
 * (apps/full-bleed/src/sparkles.js).
 *
 * Four short-lived <i> elements are appended to a single body-level layer with
 * randomised-per-preset custom properties and removed on `animationend`; the
 * keyframes live in assets/component-header.css (@keyframes ph-sparkle-pop).
 *
 * It never carries navigation or content meaning, so it is inert for
 * `prefers-reduced-motion` users and hidden from assistive tech. Clicks
 * sparkle everywhere; hover only sparkles on a fine, hover-capable pointer.
 *
 * Listeners are delegated on `document`, so nav links re-rendered by a section
 * load are covered without re-binding.
 */

const HOVER_SELECTOR = [
  // :not(.ph-header__dropdown-editorial) on both of these — the header nav
  // carries both classes at once (`class="ph-header__nav ... menu-nav"`),
  // so excluding it from only one left the other still matching. Without
  // this, the editorial tile (a big image card) sparkled anywhere across it
  // (the photo, the ornaments) instead of specifically on its own CTA pill
  // below.
  '.menu-nav a:not(.ph-header__dropdown-editorial)',
  '.ph-header__nav a:not(.ph-header__dropdown-editorial)',
  '[data-ph-tease-cta]',
  '.ph-header__cta',
  '.ph-drawer__top-link',
  '.ph-story-item__cta',
  '.ph-chapter__cta',
  ".ph-signup__form button[type='submit']",
  '.ph-header__dropdown-cta',
  // Collection page + PDP buttons that got the same treatment as the
  // homepage CTAs above.
  '.collection-pills__pill',
  '.facets-toggle__button',
  '.content-tile__cta',
  '[data-testid="standalone-add-to-cart"]',
  'variant-picker .variant-option__button-label',
  '.facets__see-results',
  '.facets__clear-all',
].join(', ');

/** Cap the layer so a held-down pointer can't grow the DOM without bound. */
const MAX_LIVE_SPARKLES = 48;

/**
 * type, offsetX, offsetY, size, tint, dx, dy, delay, duration
 * `tint` picks between the two brand tones resolved at runtime.
 * @type {Array<[string, number, number, number, 'light' | 'pink', number, number, number, number]>}
 */
const PRESETS = [
  ['glint', -10, -12, 12, 'light', -6, -5, 0, 580],
  ['glint', 9, 6, 9, 'pink', 6, 4, 45, 510],
  ['dust', 12, -7, 5, 'light', 6, -4, 70, 470],
  ['dust', -7, 8, 4, 'pink', -5, 5, 95, 460],
];

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

/** @type {HTMLElement | null} */
let layer = null;
let live = 0;

/** Reads the brand tones off the header so the trail follows the theme settings. */
function resolveColors() {
  const header = document.querySelector('[data-ph-header]');
  const styles = header ? getComputedStyle(header) : null;

  return {
    light: styles?.getPropertyValue('--ph-header-cream').trim() || '#fff',
    pink: styles?.getPropertyValue('--ph-header-accent-soft').trim() || '#ffd7ef',
  };
}

function getLayer() {
  if (layer?.isConnected) return layer;

  layer = document.createElement('div');
  layer.className = 'ph-sparkle-layer';
  layer.setAttribute('aria-hidden', 'true');
  document.body.append(layer);

  return layer;
}

/**
 * @param {number} x - viewport x
 * @param {number} y - viewport y
 */
function addSparkles(x, y) {
  if (reducedMotion.matches) return;
  if (live > MAX_LIVE_SPARKLES) return;

  const host = getLayer();
  const colors = resolveColors();

  for (const [type, offsetX, offsetY, size, tint, dx, dy, delay, duration] of PRESETS) {
    const sparkle = document.createElement('i');

    sparkle.className = `ph-sparkle ph-sparkle--${type}`;
    sparkle.style.cssText = [
      `left:${x + offsetX}px`,
      `top:${y + offsetY}px`,
      `--ph-sparkle-size:${size}px`,
      `--ph-sparkle-color:${colors[tint]}`,
      `--ph-sparkle-dx:${dx}px`,
      `--ph-sparkle-dy:${dy}px`,
      `--ph-sparkle-delay:${delay}ms`,
      `--ph-sparkle-duration:${duration}ms`,
    ].join(';');

    live += 1;
    sparkle.addEventListener(
      'animationend',
      () => {
        live -= 1;
        sparkle.remove();
      },
      { once: true }
    );

    host.append(sparkle);
  }
}

document.addEventListener('click', (event) => {
  // `detail` is 0 for keyboard-activated clicks, which have no useful point.
  if (!event.detail) return;
  addSparkles(event.clientX, event.clientY);
});

// `pointerenter` doesn't bubble, so delegate `pointerover` and ignore moves
// that stay inside the same target.
document.addEventListener('pointerover', (event) => {
  if (!finePointer.matches || event.pointerType !== 'mouse') return;

  const target = event.target instanceof Element ? event.target.closest(HOVER_SELECTOR) : null;
  if (!target) return;

  const from = event.relatedTarget;
  if (from instanceof Node && target.contains(from)) return;

  addSparkles(event.clientX, event.clientY);
});
